import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Gamepad2, Sparkles, Gem, Crown, Package, Loader2, ArrowLeft, ArrowRight, Check, Pause, Play, XCircle, DollarSign, Users, Trophy, ExternalLink, Search, Zap, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_META = {
  red: { label: 'Red Chase', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', icon: Sparkles, target: 6 },
  orange: { label: 'Orange Box', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30', icon: Gem, target: 4 },
  blue: { label: 'Blue Box', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30', icon: Crown, target: 4 },
};

const PullGameModule = () => {
  const [view, setView] = useState('list'); // list | create | detail
  const [activeGameId, setActiveGameId] = useState(null);
  const [games, setGames] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadGames = async () => {
    try {
      const [g, s] = await Promise.all([
        axios.get(`${API}/api/pull-game/admin/games`),
        axios.get(`${API}/api/pull-game/admin/stats`),
      ]);
      setGames(g.data.games || []);
      setStats(s.data || null);
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Failed to load games');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadGames(); }, []);

  if (view === 'create') return <CreateGameWizard onBack={() => setView('list')} onCreated={() => { loadGames(); setView('list'); }} />;
  if (view === 'detail' && activeGameId) return <GameDetail gameId={activeGameId} onBack={() => { setView('list'); loadGames(); }} />;

  return (
    <div data-testid="pull-game-module">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <StatCard label="Active Games" value={stats.active_games || 0} icon={Gamepad2} />
          <StatCard label="Total Games" value={stats.total_games || 0} icon={Package} />
          <StatCard label="Pulls Sold" value={stats.total_pulls_sold || 0} icon={Users} />
          <StatCard label="Revenue" value={`$${(stats.total_revenue || 0).toFixed(2)}`} icon={DollarSign} />
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <h2 className="text-base font-bold text-white">Your Pull Games</h2>
        <button
          onClick={() => setView('create')}
          data-testid="create-pull-game-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold transition-colors"
        >
          <Plus className="w-4 h-4" /> New Game
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>
      ) : games.length === 0 ? (
        <EmptyState onCreate={() => setView('create')} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {games.map(g => <GameCard key={g.id} game={g} onOpen={() => { setActiveGameId(g.id); setView('detail'); }} />)}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-[#111] border border-[#1a1a1a] rounded-xl p-4">
    <div className="flex items-center gap-2 mb-2"><Icon className="w-3.5 h-3.5 text-gray-500" /><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</span></div>
    <p className="text-2xl font-black text-white">{value}</p>
  </div>
);

const EmptyState = ({ onCreate }) => (
  <div className="text-center py-16 bg-[#111] border border-[#1a1a1a] rounded-2xl" data-testid="pull-game-empty">
    <Gamepad2 className="w-12 h-12 text-gray-700 mx-auto mb-4" />
    <h3 className="text-lg font-bold text-white mb-2">No Pull Games Yet</h3>
    <p className="text-sm text-gray-500 mb-5 max-w-sm mx-auto">Create your first interactive pull game. Pick chasers, add low-end inventory, set pricing tiers, and let buyers pull live.</p>
    <button onClick={onCreate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold transition-colors">
      <Plus className="w-4 h-4" /> Create First Game
    </button>
  </div>
);

const GameCard = ({ game, onOpen }) => {
  const soldPct = game.total_pulls > 0 ? ((game.total_pulls - game.pulls_remaining) / game.total_pulls * 100).toFixed(0) : 0;
  const statusColor = { active: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30', paused: 'text-amber-400 bg-amber-500/10 border-amber-500/30', ended: 'text-gray-400 bg-gray-500/10 border-gray-500/30' }[game.status];
  const publicUrl = `${window.location.origin}/pull-game/${game.id}`;
  return (
    <div className="bg-[#111] border border-[#1a1a1a] rounded-2xl p-5 hover:border-amber-500/30 transition-colors cursor-pointer" onClick={onOpen} data-testid={`game-card-${game.id}`}>
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-base font-bold text-white truncate">{game.name}</h3>
        <span className={`text-[9px] px-2 py-0.5 rounded border uppercase tracking-wider font-bold ${statusColor}`}>{game.status}</span>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
        <span>{game.total_pulls - (game.pulls_remaining || 0)}/{game.total_pulls} sold</span>
        <span>·</span>
        <span className="text-amber-400 font-bold">${(game.stats?.revenue || 0).toFixed(2)}</span>
        <span>·</span>
        <span>{game.chasers?.length || 0} chasers</span>
      </div>
      <div className="w-full h-1.5 bg-[#0a0a0a] rounded-full overflow-hidden mb-3">
        <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500" style={{ width: `${soldPct}%` }} />
      </div>
      <button
        onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(publicUrl); toast.success('Public link copied'); }}
        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.04] text-[11px] font-bold text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors"
        data-testid={`copy-link-${game.id}`}
      >
        <ExternalLink className="w-3 h-3" /> Copy Public Link
      </button>
    </div>
  );
};

// ============== CREATE WIZARD ==============
const CreateGameWizard = ({ onBack, onCreated }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState(`Pull Game ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
  const [totalPulls, setTotalPulls] = useState(65);
  const [tiers, setTiers] = useState([
    { from: 1, to: 10, price: 5 },
    { from: 11, to: 25, price: 8 },
    { from: 26, to: 40, price: 12 },
    { from: 41, to: 55, price: 18 },
    { from: 56, to: 65, price: 25 },
  ]);
  const [inventory, setInventory] = useState([]);
  const [selectedChasers, setSelectedChasers] = useState({}); // card_id -> tier
  const [selectedLowEnds, setSelectedLowEnds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/pull-game/inventory/available`)
      .then(r => setInventory(r.data.cards || []))
      .catch(() => toast.error('Failed to load inventory'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = inventory.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.player || '').toLowerCase().includes(q) || (c.set_name || '').toLowerCase().includes(q) || (c.card_name || '').toLowerCase().includes(q);
  });

  const toggleChaser = (id, tier) => {
    setSelectedChasers(prev => {
      const next = { ...prev };
      if (next[id] === tier) delete next[id];
      else next[id] = tier;
      return next;
    });
    // Remove from low-ends if it's now a chaser
    setSelectedLowEnds(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const toggleLowEnd = (id) => {
    if (selectedChasers[id]) return;
    setSelectedLowEnds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const submit = async () => {
    const grouped = { red: [], orange: [], blue: [] };
    Object.entries(selectedChasers).forEach(([cid, tier]) => { if (grouped[tier]) grouped[tier].push(cid); });
    if (grouped.red.length !== TIER_META.red.target) { toast.error(`Pick exactly ${TIER_META.red.target} Red Chase cards (currently ${grouped.red.length})`); return; }
    if (grouped.orange.length !== TIER_META.orange.target) { toast.error(`Pick exactly ${TIER_META.orange.target} Orange Box cards (currently ${grouped.orange.length})`); return; }
    if (grouped.blue.length !== TIER_META.blue.target) { toast.error(`Pick exactly ${TIER_META.blue.target} Blue Box cards (currently ${grouped.blue.length})`); return; }
    const minLowEnd = totalPulls - 8; // 6 red + 1 orange trigger + 1 blue trigger
    if (selectedLowEnds.size < 1) { toast.error('Add at least 1 low-end card'); return; }
    if (selectedLowEnds.size < Math.min(minLowEnd, 5)) { toast.error(`Add at least ${Math.min(minLowEnd, 5)} low-end cards (pool will recycle if total < ${minLowEnd})`); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/pull-game/admin/games`, {
        name, total_pulls: totalPulls, tiers,
        red_ids: grouped.red,
        orange_ids: grouped.orange,
        blue_ids: grouped.blue,
        low_end_ids: Array.from(selectedLowEnds),
      });
      toast.success('Pull game created!');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create game');
    } finally { setSubmitting(false); }
  };

  const inputCls = 'w-full bg-[#0a0a0a] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-amber-500/30';
  const counts = Object.values(selectedChasers).reduce((acc, t) => { acc[t] = (acc[t] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="create-pull-game-wizard">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-white/5" data-testid="wizard-back-btn"><ArrowLeft className="w-4 h-4 text-gray-400" /></button>
        <div>
          <h2 className="text-xl font-bold text-white">New Pull Game · Step {step}/2</h2>
          <p className="text-xs text-gray-500">{step === 1 ? 'Basics & pricing' : 'Pick your chasers (6 Red + 4 Orange + 4 Blue) and low-end pool'}</p>
        </div>
      </div>

      {step === 1 && (
        <div className="max-w-2xl space-y-5">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1 block">Game Name</label>
            <input className={inputCls} value={name} onChange={e => setName(e.target.value)} data-testid="game-name-input" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1 block">Total Pulls</label>
            <input type="number" min="10" max="500" className={inputCls} value={totalPulls} onChange={e => {
              const n = parseInt(e.target.value) || 65;
              setTotalPulls(n);
            }} data-testid="total-pulls-input" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block">Pricing Tiers</label>
              <button type="button" onClick={() => setTiers(ts => {
                const last = ts[ts.length - 1];
                const newFrom = (last?.to || 0) + 1;
                return [...ts, { from: newFrom, to: Math.max(newFrom, totalPulls), price: (last?.price || 5) + 5 }];
              })} className="flex items-center gap-1 text-[10px] font-bold text-amber-400 hover:text-amber-300" data-testid="add-tier-btn">
                <Plus className="w-3 h-3" /> Add Tier
              </button>
            </div>
            <div className="space-y-2">
              {tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-[#111] border border-[#1a1a1a] rounded-lg" data-testid={`tier-row-${i}`}>
                  <span className="text-[10px] text-gray-500 font-bold uppercase">Pulls</span>
                  <input type="number" min="1" className={inputCls + ' w-16 text-center'} value={t.from} onChange={e => setTiers(ts => ts.map((x, idx) => idx === i ? { ...x, from: parseInt(e.target.value) || 1 } : x))} data-testid={`tier-from-${i}`} />
                  <span className="text-xs text-gray-500">–</span>
                  <input type="number" min="1" className={inputCls + ' w-16 text-center'} value={t.to} onChange={e => setTiers(ts => ts.map((x, idx) => idx === i ? { ...x, to: parseInt(e.target.value) || 1 } : x))} data-testid={`tier-to-${i}`} />
                  <span className="text-xs text-gray-500 ml-auto">$</span>
                  <input type="number" min="0" step="0.5" className={inputCls + ' w-20 text-center'} value={t.price} onChange={e => setTiers(ts => ts.map((x, idx) => idx === i ? { ...x, price: parseFloat(e.target.value) || 0 } : x))} data-testid={`tier-price-${i}`} />
                  <span className="text-[10px] text-gray-500">USD</span>
                  {tiers.length > 1 && (
                    <button type="button" onClick={() => setTiers(ts => ts.filter((_, idx) => idx !== i))} className="p-1 rounded hover:bg-red-500/20 text-red-400" data-testid={`tier-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {(() => {
              const sorted = [...tiers].sort((a,b) => a.from - b.from);
              const issues = [];
              if (sorted[0]?.from !== 1) issues.push(`First tier should start at 1 (currently ${sorted[0]?.from})`);
              if (sorted[sorted.length-1]?.to < totalPulls) issues.push(`Last tier ends at ${sorted[sorted.length-1]?.to} but total pulls is ${totalPulls}`);
              for (let i=1; i<sorted.length; i++) {
                if (sorted[i].from !== sorted[i-1].to + 1) issues.push(`Gap/overlap between tier ${i} (ends ${sorted[i-1].to}) and tier ${i+1} (starts ${sorted[i].from})`);
              }
              return issues.length > 0 ? (
                <div className="mt-2 p-2 rounded bg-amber-500/10 border border-amber-500/30 text-[10px] text-amber-300" data-testid="tier-warnings">
                  {issues.map((m, i) => <div key={i}>⚠️ {m}</div>)}
                </div>
              ) : null;
            })()}
          </div>
          <button onClick={() => setStep(2)} className="w-full px-5 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold flex items-center justify-center gap-2" data-testid="wizard-next-1">
            Next: Pick Cards <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {step === 2 && (
        <div>
          <div className="sticky top-0 bg-[#0a0a0a] pb-3 mb-3 z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input className={inputCls + ' pl-9'} placeholder="Search player, set..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-2 text-[11px]">
              {(['red','orange','blue']).map(t => {
                const m = TIER_META[t];
                const has = counts[t] || 0;
                const ok = has === m.target;
                return (
                  <span key={t} className={`px-2 py-1 rounded-full border ${ok ? m.bg : 'bg-white/[0.02] border-white/10'} font-bold ${m.color}`}>
                    {ok ? '✓ ' : ''}{m.label}: {has}/{m.target}
                  </span>
                );
              })}
              <span className="px-2 py-1 rounded-full border border-white/10 bg-white/[0.02] text-emerald-400 font-bold">
                Low-End: {selectedLowEnds.size}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">Click a tier button to mark a card as that chaser. Click the card body itself to add as low-end.</p>
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {filtered.map(c => (
                <CardPicker key={c.id} card={c}
                  chaserTier={selectedChasers[c.id]}
                  isLowEnd={selectedLowEnds.has(c.id)}
                  onToggleChaser={tier => toggleChaser(c.id, tier)}
                  onToggleLowEnd={() => toggleLowEnd(c.id)}
                />
              ))}
            </div>
          )}
          <div className="sticky bottom-0 bg-[#0a0a0a] pt-3 mt-3 border-t border-[#1a1a1a] flex gap-2">
            <button onClick={() => setStep(1)} className="px-5 py-3 rounded-lg bg-white/5 text-gray-400 text-sm font-bold"><ArrowLeft className="w-4 h-4 inline mr-1" /> Back</button>
            <button onClick={submit} disabled={submitting} className="flex-1 px-5 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:bg-gray-700 disabled:cursor-not-allowed text-black text-sm font-bold flex items-center justify-center gap-2" data-testid="wizard-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {submitting ? 'Creating...' : 'Launch Game'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CardPicker = ({ card, chaserTier, isLowEnd, onToggleChaser, onToggleLowEnd }) => {
  const img = card.store_thumbnail || card.thumbnail;
  const tier = chaserTier ? TIER_META[chaserTier] : null;
  return (
    <div className={`relative rounded-lg overflow-hidden border-2 transition-all ${tier ? tier.bg : isLowEnd ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-white/[0.04] bg-white/[0.02]'}`} data-testid={`card-picker-${card.id}`}>
      <div className="aspect-square bg-[#111] cursor-pointer" onClick={onToggleLowEnd}>
        {img ? <img src={`data:image/jpeg;base64,${img}`} className="w-full h-full object-cover" alt="" /> : <Package className="w-8 h-8 text-gray-700 m-auto mt-8" />}
        {tier && <span className={`absolute top-1 left-1 text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase ${tier.color} bg-black/60`}>{tier.label}</span>}
        {isLowEnd && !tier && <span className="absolute top-1 left-1 text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase text-emerald-400 bg-black/60">Low-End</span>}
      </div>
      <div className="p-2">
        <p className="text-[10px] font-bold text-white truncate">{card.player || card.card_name || 'Card'}</p>
        <p className="text-[9px] text-gray-500 truncate">{card.year} {card.set_name}</p>
        <p className="text-[9px] text-amber-400 font-bold">${(card.card_value || 0).toFixed(2)}</p>
      </div>
      <div className="flex gap-1 p-1 bg-[#0a0a0a]">
        {['red', 'orange', 'blue'].map(t => {
          const m = TIER_META[t];
          const Icon = m.icon;
          const active = chaserTier === t;
          return (
            <button key={t} onClick={() => onToggleChaser(t)}
              className={`flex-1 py-1 rounded text-[9px] font-bold transition-colors ${active ? `${m.color} bg-white/10` : 'text-gray-600 hover:text-gray-400'}`}
              data-testid={`tier-btn-${t}-${card.id}`}
            >
              <Icon className="w-2.5 h-2.5 inline" /> {t.toUpperCase()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============== GAME DETAIL ==============
const GameDetail = ({ gameId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const publicUrl = `${window.location.origin}/pull-game/${gameId}`;

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/pull-game/admin/games/${gameId}`);
      setData(r.data);
    } catch { toast.error('Failed to load game'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [gameId]);

  const changeStatus = async (status) => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/api/pull-game/admin/games/${gameId}`, { status });
      toast.success(`Game ${status}`);
      load();
    } catch { toast.error('Failed'); }
    finally { setUpdating(false); }
  };

  const markShipped = async (pullNumber) => {
    try {
      await axios.post(`${API}/api/pull-game/admin/games/${gameId}/ship/${pullNumber}`, {});
      toast.success('Marked shipped');
      load();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>;
  if (!data) return null;

  const { game, spots } = data;
  const claimed = spots.filter(s => s.status === 'claimed');

  return (
    <div data-testid="game-detail">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-white/5"><ArrowLeft className="w-4 h-4 text-gray-400" /></button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-white">{game.name}</h2>
          <p className="text-xs text-gray-500">{game.total_pulls} pulls · ${game.stats?.revenue?.toFixed(2) || '0.00'} revenue</p>
        </div>
        <button
          onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied'); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold text-gray-300"
          data-testid="copy-public-url"
        ><ExternalLink className="w-3 h-3" /> Copy Public URL</button>
        {game.status === 'active' && (
          <button onClick={() => changeStatus('paused')} disabled={updating} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-bold"><Pause className="w-3 h-3" /> Pause</button>
        )}
        {game.status === 'paused' && (
          <button onClick={() => changeStatus('active')} disabled={updating} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold"><Play className="w-3 h-3" /> Resume</button>
        )}
        {game.status !== 'ended' && (
          <button onClick={() => { if (confirm('End this game permanently?')) changeStatus('ended'); }} disabled={updating} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-bold"><XCircle className="w-3 h-3" /> End</button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Pulls Sold" value={game.stats?.pulls_sold || 0} icon={Users} />
        <StatCard label="Revenue" value={`$${(game.stats?.revenue || 0).toFixed(2)}`} icon={DollarSign} />
        <StatCard label="Remaining" value={spots.filter(s => s.status === 'available').length} icon={Package} />
        <StatCard label="Status" value={game.status} icon={Trophy} />
      </div>

      {/* Winners */}
      {claimed.filter(s => s.is_chaser).length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2"><Trophy className="w-3.5 h-3.5" /> Chasers Claimed ({claimed.filter(s => s.is_chaser).length})</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {claimed.filter(s => s.is_chaser).map(s => {
              const tier = TIER_META[s.chaser_tier];
              return (
                <div key={s.id} className={`p-3 rounded-lg border ${tier?.bg}`}>
                  <p className={`text-[10px] font-bold ${tier?.color}`}>#{s.pull_number} · {tier?.label}</p>
                  <p className="text-sm font-bold text-white truncate">{s.card_snapshot?.title}</p>
                  <p className="text-xs text-gray-400">{s.claimed_by_email}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Order list */}
      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Orders ({claimed.length})</h3>
      <div className="bg-[#111] border border-[#1a1a1a] rounded-xl divide-y divide-[#1a1a1a]">
        {claimed.map(s => (
          <div key={s.id} className="p-3 flex items-center gap-3">
            <span className="text-[10px] font-bold text-gray-500 w-10">#{s.pull_number}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white truncate">{s.card_snapshot?.title}</p>
              <p className="text-[11px] text-gray-500">{s.claimed_by_email} · {s.shipping_address?.first_name} {s.shipping_address?.last_name}</p>
            </div>
            <span className="text-sm font-bold text-amber-400">${s.price}</span>
            {s.shipped_at ? (
              <span className="text-[10px] px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 font-bold">SHIPPED</span>
            ) : (
              <button onClick={() => markShipped(s.pull_number)} className="text-[10px] px-3 py-1.5 rounded bg-blue-500/10 text-blue-400 font-bold hover:bg-blue-500/20">Mark Shipped</button>
            )}
          </div>
        ))}
        {claimed.length === 0 && <p className="p-4 text-sm text-gray-500 text-center">No orders yet</p>}
      </div>
    </div>
  );
};

export default PullGameModule;
